import { useEffect, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';

/** Nothing to show yet, with what to do next. */
export function EmptyState({ title, description, action }: { title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <section className="bb-state" aria-label={title}>
      <h2 className="bb-state__title">{title}</h2>
      {description && <p>{description}</p>}
      {action}
    </section>
  );
}

/** Loading failed; shows the problem code and request id when the API returned them. */
export function ErrorState({ title = 'Something went wrong', message, code, requestId, onRetry }: { title?: string; message?: ReactNode; code?: string; requestId?: string; onRetry?: () => void }) {
  return (
    <section className="bb-state" role="alert">
      <h2 className="bb-state__title">{title}</h2>
      {message && <p>{message}</p>}
      {(code || requestId) && (
        <p className="bb-field__hint">
          {code && <code>{code}</code>}
          {code && requestId && ' · '}
          {requestId && <>request {requestId}</>}
        </p>
      )}
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </section>
  );
}

/** A page the employee may not use; names the missing permission so an admin can grant it. */
export function PermissionDenied({ permission }: { permission: string }) {
  return (
    <section role="alert" className="bb-state">
      <h1 className="bb-state__title">No access</h1>
      <p>
        This page needs the <code>{permission}</code> permission. Ask an organization admin to grant it.
      </p>
    </section>
  );
}

/** Explains why fields cannot be edited (e.g. locked after launch). */
export function ReadOnlyBanner({ children }: { children: ReactNode }) {
  return (
    <div className="bb-banner bb-tone--warning" role="note">
      <span aria-hidden="true">🔒</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Warns before the page is closed with unsaved changes (browser `beforeunload`). Only active while `isDirty`;
 * in-app navigation uses the router's blocker with `confirmLeave`.
 */
export function UnsavedChangesGuard({ isDirty }: { isDirty: boolean }) {
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);
  return null;
}

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Leave this page and discard them?';

/** For router blockers: asks only when there are unsaved changes. */
export function confirmLeave(isDirty: boolean, confirm: (message: string) => boolean = (m) => window.confirm(m)): boolean {
  return !isDirty || confirm(UNSAVED_CHANGES_MESSAGE);
}
